import { useState, useMemo, useRef, useEffect, useCallback } from "react";

/**
 * Generic "search marks and jumps" hook for the OS stage tables
 * (OSRazmerTable / OSProductivityTable / OSMaterialnoTable).
 *
 * The table computes its own list of searchable cells (in DISPLAY order — after any
 * view-sort has been applied) and hands them in as `cells`. This hook never touches
 * row order, never filters/hides rows — it only tracks which cell ids match the term
 * and which one is "active" (so the table can highlight + scroll to it).
 *
 * Deliberately generic: no knowledge of RazmerRow/ProdRow/MatGroup, just
 * `{ id, text }` pairs the caller has already flattened.
 */
export interface SearchCell {
  id: string;
  text: string;
}

export const useTableSearchHighlight = (cells: SearchCell[]) => {
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const cellRefs = useRef<Map<string, HTMLElement | null>>(new Map());

  // Ids of matching cells, in the same order `cells` was handed in (i.e. display order) —
  // this is what next()/prev() cycle through and what activeId is derived from.
  const matchOrder = useMemo(() => {
    const term = searchTerm.trim().toLocaleLowerCase();
    if (!term) return [] as string[];
    return cells
      .filter((c) => (c.text || "").toLocaleLowerCase().includes(term))
      .map((c) => c.id);
  }, [cells, searchTerm]);

  const matchIds = useMemo(() => new Set(matchOrder), [matchOrder]);
  const matchCount = matchOrder.length;

  // Keep activeIndex in range whenever the match set changes size (new term, or the
  // underlying cells changed while a term was already active).
  useEffect(() => {
    setActiveIndex((prev) => {
      if (matchCount === 0) return -1;
      if (prev < 0) return 0;
      if (prev >= matchCount) return matchCount - 1;
      return prev;
    });
  }, [matchCount]);

  const activeId = activeIndex >= 0 && activeIndex < matchOrder.length ? matchOrder[activeIndex] : null;

  const next = useCallback(() => {
    setActiveIndex((prev) => {
      if (matchOrder.length === 0) return -1;
      return (prev + 1) % matchOrder.length;
    });
  }, [matchOrder]);

  const prev = useCallback(() => {
    setActiveIndex((prevIndex) => {
      if (matchOrder.length === 0) return -1;
      return (prevIndex - 1 + matchOrder.length) % matchOrder.length;
    });
  }, [matchOrder]);

  // Enter (or clicking the search button) on the SAME term jumps to the next match —
  // committing a new term always resets to the first match.
  const handleSearch = useCallback(() => {
    if (searchInput === searchTerm && searchTerm.trim() !== "") {
      next();
      return;
    }
    setSearchTerm(searchInput);
    setActiveIndex(0);
  }, [searchInput, searchTerm, next]);

  const clearSearch = useCallback(() => {
    setSearchInput("");
    setSearchTerm("");
    setActiveIndex(-1);
  }, []);

  const registerCell = useCallback(
    (id: string) => (el: HTMLElement | null) => {
      if (el) cellRefs.current.set(id, el);
      else cellRefs.current.delete(id);
    },
    []
  );

  // Scroll the active match into view. Never .focus() — focusing would open the cell
  // for editing, which search must never do.
  useEffect(() => {
    if (!activeId) return;
    const el = cellRefs.current.get(activeId);
    el?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [activeId]);

  const isMatch = useCallback((id: string) => matchIds.has(id), [matchIds]);
  const isActive = useCallback((id: string) => id === activeId, [activeId]);

  return {
    searchInput,
    setSearchInput,
    searchTerm,
    handleSearch,
    clearSearch,
    matchIds,
    matchCount,
    activeIndex,
    activeId,
    next,
    prev,
    registerCell,
    isMatch,
    isActive,
  };
};
